import { useMediaQuery } from '@mui/material';
import React, { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StoreSalesPerformanceResponse, useStoreInformation } from '../../../../../hooks';
import { Typography } from '../../../../../stories/atoms';
import { ColumnFlexContainer } from '../../../../../stories/atoms/containerV2/ContainerV2';
import { ContainerDrawer } from '../../../../../stories/molecules/container-drawer/ContainerDrawer';
import { HeaderRowItem, Table } from '../../../../../stories/molecules/tablev2/Table';
import { muiTheme } from '../../../../../theme';
import { commonTranslations, storeOverviewTranslations } from '../../../../../translations';
import { formatAmountWithSeparator } from '../../../../../utils';
import { StoreWidgetContent } from '../../../../storeOverviewPage/storeWidgetContent/StoreWidgetContent';
import { ToggleChipGroup } from '../../../common/toggleChipGroup/ToggleChipGroup';
import { HeaderTooltip } from '../../headerTooltip/HeaderTooltip';
import { StyledIndexTypography } from '../mobileChipLayout/MobileChipLayout.styles';
import { NoDataForPeriodView } from '../noDataForPeriodView/NoDataForPeriodView';
import {
  chipDataMapping,
  ChipKey,
  ChipOption,
  getIndexStatus,
  getSalesByIndexData,
  SalesAggregatedRow,
  useChipOptionsMap,
} from '../sales.util';
import { SalesSummaryCard, SalesSummarySlider } from '../salesSummaryCard/SalesSummaryCard';

type SalesByIndexWidgetProps = {
  isLoading: boolean;
  isError: boolean;
  salesPerformanceData: StoreSalesPerformanceResponse | null;
  isExpanded: boolean;
  expandButton: React.ReactNode;
  selectedFilter: string | undefined;
  retry: () => void;
};

enum SalesByIndexColumnType {
  METRIC = 'metric',
  SALESNETTHISYEAR = 'salesnetTY',
  DIFFERENCELASTYEAR = 'diffLY',
  SOB = 'sob',
  DRIVINGUNIT = 'du',
  INDEXLASTYEAR = 'indexLY',
  LASTYEARVSAREA = 'lyVsArea',
  LASTYEARVSSALESMARKET = 'lyVsSalesMarket',
}

export const SalesByIndexWidget: FC<SalesByIndexWidgetProps> = ({
  isLoading,
  isError,
  salesPerformanceData,
  isExpanded,
  expandButton,
  selectedFilter,
  retry,
}) => {
  const { t } = useTranslation();
  const { chipOptionsMap } = useChipOptionsMap();
  const { salesByIndex: salesByIndexTranslations, headerTooltips: headerTooltipsTranslations } =
    storeOverviewTranslations;
  const { corporateBrandId } = useStoreInformation();
  const isHMBrand = corporateBrandId === 0;

  const isSmallDesktop = useMediaQuery(muiTheme.breakpoints.down('desktop'));
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('tabletPortrait'));

  const [isSliderOpen, setIsSliderOpen] = useState(false);
  const [noDataView, setNoDataView] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState<SalesAggregatedRow | null>(null);
  const [selectedChip, setSelectedChip] = useState<ChipKey>('LCD');
  const [totalRow, setTotalRow] = useState<SalesAggregatedRow>({
    metric: '',
    salesNetThisYear: 0,
    differenceLastYear: 0,
    sob: 0,
    drivingUnit: 0,
    indexLastYear: 0,
    lastYearVsArea: 0,
    lastYearVsSalesMarket: 0,
  });

  const salesByIndexData = useMemo(
    () => (salesPerformanceData ? getSalesByIndexData(salesPerformanceData, selectedFilter) : null),
    [salesPerformanceData, selectedFilter],
  );

  const tableData = useMemo(() => {
    if (!salesByIndexData) return [];

    const selectedIndexData = salesByIndexData[chipDataMapping[selectedChip]] || {};
    const { total, ...rest } = selectedIndexData;

    setTotalRow(total);
    const rowLength = Object.values(rest).length;

    setNoDataView(rowLength === 0);
    return Object.values(rest);
  }, [salesByIndexData, selectedChip]);

  const headerItems: HeaderRowItem<SalesByIndexColumnType>[] = useMemo(() => {
    return [
      {
        id: SalesByIndexColumnType.METRIC,
        content: selectedFilter ? (
          <Typography type="body3">
            {t(storeOverviewTranslations.subIndexLabel.key, storeOverviewTranslations.subIndexLabel.defaultValue)}
          </Typography>
        ) : (
          <Typography type="body3">
            {t(
              storeOverviewTranslations.indexFilterLabel.key,
              storeOverviewTranslations.indexFilterLabel.defaultValue,
            )}
          </Typography>
        ),
        width: '20%',
      },
      {
        id: SalesByIndexColumnType.SALESNETTHISYEAR,
        content: (
          <HeaderTooltip
            headerText={t(
              storeOverviewTranslations.salesNetThisYear.key,
              storeOverviewTranslations.salesNetThisYear.defaultValue,
            )}
            tooltipText={t(
              headerTooltipsTranslations.salesNetTY.key,
              headerTooltipsTranslations.salesNetTY.defaultValue,
            )}
          />
        ),
        width: isHMBrand ? '15%' : '20%',
      },
      {
        id: SalesByIndexColumnType.DIFFERENCELASTYEAR,
        content: (
          <HeaderTooltip
            headerText="Diff LY"
            tooltipText={t(
              headerTooltipsTranslations.diffMny.key,
              headerTooltipsTranslations.diffMny.defaultValue,
            )}
          />
        ),
        width: isHMBrand ? '15%' : '20%',
      },
      {
        id: SalesByIndexColumnType.SOB,
        content: (
          <HeaderTooltip
            headerText="SOB"
            tooltipText={t(
              headerTooltipsTranslations.sobPercent.key,
              headerTooltipsTranslations.sobPercent.defaultValue,
            )}
          />
        ),
        width: '10%',
      },
      {
        id: SalesByIndexColumnType.DRIVINGUNIT,
        content: (
          <HeaderTooltip
            headerText="DU"
            tooltipText={t(
              headerTooltipsTranslations.drivingUnit.key,
              headerTooltipsTranslations.drivingUnit.defaultValue,
            )}
          />
        ),
        width: '10%',
      },
      {
        id: SalesByIndexColumnType.INDEXLASTYEAR,
        content: (
          <HeaderTooltip
            headerText="vs LY"
            tooltipText={t(headerTooltipsTranslations.vsLY.key, headerTooltipsTranslations.vsLY.defaultValue)}
          />
        ),
        width: '10%',
      },
      {
        id: SalesByIndexColumnType.LASTYEARVSAREA,
        content: (
          <HeaderTooltip
            headerText="vs Area"
            tooltipText={t(headerTooltipsTranslations.vsArea.key, headerTooltipsTranslations.vsArea.defaultValue)}
          />
        ),
        width: '10%',
      },
      ...(isHMBrand
        ? [
            {
              id: SalesByIndexColumnType.LASTYEARVSSALESMARKET,
              content: (
                <HeaderTooltip
                  headerText="vs SM"
                  tooltipText={t(
                    headerTooltipsTranslations.vsSM.key,
                    headerTooltipsTranslations.vsSM.defaultValue,
                  )}
                />
              ),
              width: '10%',
            },
          ]
        : []),
    ];
  }, [selectedFilter]);

  const chipOptions: ChipOption[] = [
    chipOptionsMap.LCD,
    chipOptionsMap.CW,
    chipOptionsMap.LCW,
    chipOptionsMap.L4Wks,
    chipOptionsMap.AccTY,
  ];

  const getTableRow = (item: SalesAggregatedRow) => {
    const {
      salesNetThisYear,
      differenceLastYear,
      sob,
      drivingUnit,
      indexLastYear,
      lastYearVsArea,
      lastYearVsSalesMarket,
    } = item;

    return [
      {
        id: SalesByIndexColumnType.SALESNETTHISYEAR,
        content: <Typography>{formatAmountWithSeparator(Math.round(salesNetThisYear))}</Typography>,
      },
      {
        id: SalesByIndexColumnType.DIFFERENCELASTYEAR,
        content: <Typography>{formatAmountWithSeparator(Math.round(differenceLastYear))}</Typography>,
      },
      {
        id: SalesByIndexColumnType.SOB,
        content: <Typography>{formatAmountWithSeparator(sob, '%', 1)}</Typography>,
      },
      {
        id: SalesByIndexColumnType.DRIVINGUNIT,
        content: <Typography>{formatAmountWithSeparator(drivingUnit, '%', 1, false)}</Typography>,
      },
      {
        id: SalesByIndexColumnType.INDEXLASTYEAR,
        content: (
          <StyledIndexTypography status={getIndexStatus(indexLastYear)}>{indexLastYear}</StyledIndexTypography>
        ),
      },
      {
        id: SalesByIndexColumnType.LASTYEARVSAREA,
        content: (
          <StyledIndexTypography status={getIndexStatus(lastYearVsArea)}>{lastYearVsArea}</StyledIndexTypography>
        ),
      },
      ...(isHMBrand
        ? [
            {
              id: SalesByIndexColumnType.LASTYEARVSSALESMARKET,
              content: (
                <StyledIndexTypography status={getIndexStatus(lastYearVsSalesMarket)}>
                  {lastYearVsSalesMarket}
                </StyledIndexTypography>
              ),
            },
          ]
        : []),
    ];
  };

  const handleCardDetails = (cardDetails: SalesAggregatedRow) => {
    setIsSliderOpen(true);
    setSelectedRowData(cardDetails);
  };

  return (
    <ContainerDrawer
      isOpen={isSliderOpen}
      onClose={() => setIsSliderOpen(false)}
      borderRadius={[2]}
      sliderContent={
        <SalesSummarySlider
          data={selectedRowData}
          subText={
            selectedFilter
              ? t(salesByIndexTranslations.sliderTitle2.key, salesByIndexTranslations.sliderTitle2.defaultValue)
              : t(salesByIndexTranslations.sliderTitle1.key, salesByIndexTranslations.sliderTitle1.defaultValue)
          }
        />
      }
      width={'300px'}
    >
      <StoreWidgetContent
        widgetVariant="tall"
        title={
          selectedFilter
            ? t(salesByIndexTranslations.title2.key, salesByIndexTranslations.title2.defaultValue)
            : t(salesByIndexTranslations.title1.key, salesByIndexTranslations.title1.defaultValue)
        }
        tooltipText={t(salesByIndexTranslations.tooltip.key, salesByIndexTranslations.tooltip.defaultValue)}
        isLoading={isLoading}
        isError={isError}
        errorRetryHandler={retry}
        isNoData={!salesByIndexData}
        isExpanded={isExpanded}
        rightContainerElements={isSmallDesktop ? null : expandButton}
        errorText={t(salesByIndexTranslations.errorText.key, salesByIndexTranslations.errorText.defaultValue)}
        widgetContent={
          <ColumnFlexContainer height="100%">
            <ToggleChipGroup
              chipOptions={chipOptions}
              selectedChip={selectedChip}
              setSelectedChip={(chip: ChipKey) => {
                setSelectedChip(chip);
              }}
            />
            {noDataView && <NoDataForPeriodView />}
            {!isMobile && !noDataView && salesByIndexData && (
              <ColumnFlexContainer padding={[0, 0, 2]} flex={1} data-testid="sales-by-index-table-wrapper">
                <Table
                  data-testid="sales-by-index-table"
                  items={tableData}
                  headerItems={headerItems}
                  stickyHeader
                  stickyFirstColumn
                  fixedHeight={isSmallDesktop ? '260px' : isExpanded ? '600px' : '400px'}
                  sxProps={{ padding: '8px' }}
                  headerSxProps={{ padding: '8px' }}
                  footerSxProps={{ padding: '16px 8px' }}
                  getRowItems={(item) => {
                    const row = getTableRow(item);
                    return [
                      {
                        id: SalesByIndexColumnType.METRIC,
                        content: <Typography type="body3">{item.metric}</Typography>,
                      },
                      ...row,
                    ];
                  }}
                  footerItems={[
                    {
                      id: SalesByIndexColumnType.METRIC,
                      content: (
                        <Typography type="body3">
                          {t(commonTranslations.total.key, commonTranslations.total.defaultValue)}
                        </Typography>
                      ),
                    },
                    ...getTableRow(totalRow),
                  ]}
                />
              </ColumnFlexContainer>
            )}
            {isMobile && !noDataView && salesByIndexData && (
              <ColumnFlexContainer data-testid="sales-by-index-list">
                <ColumnFlexContainer gap={[2]} padding={[0, 0, 2]} overflow="scroll" height={[54]}>
                  {tableData.map((item) => (
                    <SalesSummaryCard key={item.metric} data={item} isTotal={false} onClick={handleCardDetails} />
                  ))}
                </ColumnFlexContainer>
                <SalesSummaryCard data={totalRow} isTotal onClick={handleCardDetails} />
              </ColumnFlexContainer>
            )}
          </ColumnFlexContainer>
        }
      />
    </ContainerDrawer>
  );
};
